import React, { useEffect, useState } from 'react';
import { Title, Text, Link, Space, Modal, Checkbox, Switch } from 'components';
import { ListingLabelContext } from 'contexts';
import { INotificationSettings, ITypesNotificationSettings } from '@bb/users';
import { capitalize, cloneDeep } from 'lodash';
import { StyledModal, StyledModalWidth } from './constants';

type ListingTypeFilterProps = {
  listingTypes: INotificationSettings['types'];
  setModifiedNotifications: React.Dispatch<
    React.SetStateAction<INotificationSettings>
  >;
};

const LISTING_TYPE_OPTIONS: (keyof Omit<
  ITypesNotificationSettings,
  'lease'
>)[] = ['condo', 'freehold', 'land', 'income', 'commercial'];

const ListingTypeFilter: React.FC<ListingTypeFilterProps> = ({
  listingTypes,
  setModifiedNotifications,
}) => {
  const { getLabel } = React.useContext(ListingLabelContext);

  const [editOpen, setEditOpen] = useState(false);
  const [modifiedListingTypes, setModifiedListingTypes] = useState(
    cloneDeep(listingTypes),
  );

  useEffect(() => {
    // Reinitialize when the modal is open / closed
    setModifiedListingTypes(cloneDeep(listingTypes));
  }, [editOpen, listingTypes]);

  const includedListingTypesText =
    Array.from<keyof ITypesNotificationSettings>([
      ...LISTING_TYPE_OPTIONS,
      'lease',
    ])
      .filter(type => listingTypes[type])
      .map(type => capitalize(getLabel(type)))
      .join(', ') || 'None';

  const onChange = (type: keyof ITypesNotificationSettings, value: boolean) => {
    setModifiedListingTypes(prev => ({
      ...prev,
      [type]: value,
    }));
  };

  const onOk = () => {
    setModifiedNotifications(prev => ({
      ...prev,
      types: modifiedListingTypes,
    }));

    setEditOpen(false);
  };

  return (
    <>
      <Space direction="vertical" size={'large'}>
        <Space direction="vertical">
          <Title level={3}>Transaction / Listing Types</Title>
          <Text>
            You can choose to filter notifications below by transaction and / or
            listing types:
          </Text>
        </Space>

        <Space direction="horizontal" size={'middle'} align={'start'}>
          <Text type="secondary">Included: {includedListingTypesText}</Text>
          <Link onClick={() => setEditOpen(true)} nowrap>
            Edit
          </Link>
        </Space>
      </Space>

      <StyledModal
        title="Transaction / Listing Types"
        width={StyledModalWidth}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={onOk}
      >
        <Space direction="vertical" size={'middle'}>
          <Text>
            For sale listings, select the listing types for which you want to be
            notified:
          </Text>

          <Text strong>Sale Listings</Text>
          <Space direction="vertical" size={'small'}>
            {LISTING_TYPE_OPTIONS.map(type => (
                <Checkbox
                  key={type}
                  checked={Boolean(modifiedListingTypes[type])}
                  onChange={event => onChange(type, event.target.checked)}
                >
                  {getLabel(type)}
                </Checkbox>
              ))}
          </Space>

          <Text strong>Allow Lease Listings?</Text>
          <Text>
            By turning ON the toggle below, you will be notified of all listing
            types that are also lease listings:
          </Text>

          <Space direction="horizontal" size={0}>
            <Switch
              size="small"
              checked={modifiedListingTypes.lease}
              onChange={checked => onChange('lease', checked)}
            />
            <Text>Lease</Text>
          </Space>
        </Space>
      </StyledModal>
    </>
  );
};

export default ListingTypeFilter;
